import { sendUnitySession } from "../../api/unitySession";
import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import axios from "axios";
import Env from "../../runtimeEnv";
import { markUnityRoomPreloaded } from "../../utils/unityWebglPreloader";

const currentDomain = window.location.hostname;
const isProduction = currentDomain === "plutonichealth.co";
const developmentUnityOrigin = currentDomain === "featureenv.plutonichealth.co"
  ? window.location.origin
  : "https://devenv.plutonichealth.co";

const roomBaseUrls = isProduction
  ? {
      beach: `https://plutonichealth.co/beach`,
      mountain: `https://plutonichealth.co/forest`,
      park: `https://plutonichealth.co/park`,
      campsite: `https://plutonichealth.co/campsite`,
      winterHoliday: `https://plutonichealth.co/winter-holiday`,
      "park-therapy": `https://plutonichealth.co/park`,
      "beach-therapy": `https://plutonichealth.co/beach`,
      "forest-therapy": `https://plutonichealth.co/forest`,
      "campsite-therapy": `https://plutonichealth.co/campsite`,
      "winter-holiday": `https://plutonichealth.co/winter-holiday`,
      "demo": `https://plutonichealth.co/demo`,
    }
  : {
      beach: `${developmentUnityOrigin}/devenv-plutonictherapy/beach-dev/index.html`,
      mountain: `${developmentUnityOrigin}/devenv-plutonictherapy/forest-dev/index.html`,
      park: `${developmentUnityOrigin}/devenv-plutonictherapy/park-dev/index.html`,
      campsite: `${developmentUnityOrigin}/devenv-plutonictherapy/campsite-dev/index.html`,
      winterHoliday: `${developmentUnityOrigin}/devenv-plutonictherapy/winter-holiday-dev/index.html`,
      "park-therapy": `${developmentUnityOrigin}/devenv-plutonictherapy/park-dev/index.html`,
      "beach-therapy": `${developmentUnityOrigin}/devenv-plutonictherapy/beach-dev/index.html`,
      "forest-therapy": `${developmentUnityOrigin}/devenv-plutonictherapy/forest-dev/index.html`,
      "campsite-therapy": `${developmentUnityOrigin}/devenv-plutonictherapy/campsite-dev/index.html`,
      "winter-holiday": `${developmentUnityOrigin}/devenv-plutonictherapy/winter-holiday-dev/index.html`,
      "demo-dev": `https://featureenv.plutonichealth.co/devenv-plutonictherapy/demo-dev/index.html`,
      "demo": `https://featureenv.plutonichealth.co/devenv-plutonictherapy/demo-dev/index.html`,
      "beach-ai-companion":
        "https://featureenv.plutonichealth.co/devenv-ai-plutonictherapy/beach-ai-dev/index.html",
      "beach-ai-dev":
        "https://featureenv.plutonichealth.co/devenv-ai-plutonictherapy/beach-ai-dev/index.html",
      "test-ai-dev":
        "https://featureenv.plutonichealth.co/devenv-ai-plutonictherapy/test-ai-dev/index.html",

    };

function UnityRoom() {
  const navigate = useNavigate();
  const { roomType } = useParams();
  const [roomUrl, setRoomUrl] = useState("");
  const [isValidSession, setIsValidSession] = useState(false);
  const {
    currentNickname,
    setCurrentNickname,
    handleLogout,
    setCurrentFormType,
  } = useContext(AuthContext);

  useEffect(() => {
    const onUnityLoaded = (event) => {
      if (event.data?.type === "unityLoaded") {
        window.unityIsReady = true;
        markUnityRoomPreloaded(roomType);
      }
    };

    window.addEventListener("message", onUnityLoaded);
    return () => window.removeEventListener("message", onUnityLoaded);
  }, [roomType]);


  useEffect(() => {
    const iframe = document.getElementById("unity-iframe");
    if (!iframe) return;

    const handleEnter = () => {
      iframe.contentWindow?.postMessage({ type: "cursorShow" }, "*");
    };

    const handleLeave = () => {
      if (window.unityIsReady) {
        iframe.contentWindow?.postMessage({ type: "cursorHide" }, "*");
      }
    };


    const handleWindowLeave = (e) => {
      if (!e.relatedTarget && !e.toElement) {
        iframe.contentWindow?.postMessage({ type: "cursorHide" }, "*");
      }
    };

    iframe.addEventListener("mouseenter", handleEnter);
    iframe.addEventListener("mouseleave", handleLeave);
    window.addEventListener("mouseout", handleWindowLeave);

    return () => {
      iframe.removeEventListener("mouseenter", handleEnter);
      iframe.removeEventListener("mouseleave", handleLeave);
      window.removeEventListener("mouseout", handleWindowLeave);
    };
  }, [roomUrl]);



  
  // 🔹 Validate session & set Unity room URL
  useEffect(() => {
    const savedRoomId = sessionStorage.getItem("meetingID");
    const savedUserId = sessionStorage.getItem("userId");

    if (savedRoomId && savedUserId && roomType && roomBaseUrls[roomType]) {
      setRoomUrl(
        `${roomBaseUrls[roomType]}?roomId=${savedRoomId}&userId=${savedUserId}`
      );
      setIsValidSession(true);
    } else {
      console.warn("UnityRoom.js: warn event");
      setIsValidSession(false);
      navigate("/therapy-rooms");
    }
  }, [roomType, navigate]);

  // 🔹 Handle leaving page or tab close
  useEffect(() => {
    const isAICompanionPage = window.location.pathname.includes("ai-companion");

    window.unityInitiatedLeave = false;

    const handleOffline = () => {
      if (window.unityInitiatedLeave) {
        return;
      }

      if (window.sentOffline) return;
      window.sentOffline = true;

      const lastActiveTime = new Date().toISOString();
      localStorage.setItem("lastActiveTime", lastActiveTime);

      const meetingID = sessionStorage.getItem("meetingID");
      const userId = sessionStorage.getItem("userId");

      if (meetingID && userId) {
        const payload = { meetingID, online: false };

        navigator.sendBeacon(
          `${Env.REACT_APP_PYTHON_API_URL}/api/set-online-status/${userId}`,
          JSON.stringify(payload)
        );
      } else {
        console.warn("UnityRoom.js: warn event");
      }
    };

    const handleUnityLeave = () => {
      window.unityInitiatedLeave = true;
      handleOffline();
    };

    if (isAICompanionPage) {
      window.addEventListener("beforeunload", handleOffline);
      window.addEventListener("unityLeaveSession", handleUnityLeave);
    } else {
    }

    return () => {
      if (isAICompanionPage) {
        handleOffline();
        window.removeEventListener("beforeunload", handleOffline);
        window.removeEventListener("unityLeaveSession", handleUnityLeave);
        setTimeout(() => (window.sentOffline = false), 200);
      }
    };
  }, []);


  useEffect(() => {
    if (!isValidSession || !roomUrl) return undefined;
    const timer = setInterval(() => {
      void sendUnitySession(document.getElementById("unity-iframe"));
    }, 60000);
    return () => clearInterval(timer);
  }, [isValidSession, roomUrl]);

  // 🔹 Handle Unity iframe communication
  useEffect(() => {
    const setupUnityCommunication = () => {
      const unityIframe = document.getElementById("unity-iframe");
      if (!unityIframe) return;

      const handleUnityMessage = async (event) => {
        const allowedOrigin = new URL(roomUrl).origin;


        if (event.source !== unityIframe.contentWindow || event.origin !== allowedOrigin) {
          console.warn("UnityRoom.js: warn event");
          return;
        }

        if (event.data?.type === "unityReady") {
          markUnityRoomPreloaded(roomType);
          // Fetch a fresh authenticated session for this frame.

          if (unityIframe.contentWindow) {
            setTimeout(() => {
              void sendUnitySession(unityIframe);
            }, 300);
          }
        } else if (event.data?.type === "leaveSession") {
          window.dispatchEvent(new Event("unityLeaveSession"));

          const userId = sessionStorage.getItem("userId");
          const roomId = sessionStorage.getItem("meetingID");

          if (userId && roomId) {
            try {
              await axios.post(
                `${Env.REACT_APP_PYTHON_API_URL}/api/set-online-status/${userId}`,
                { meetingID: roomId, online: false }
              , { withCredentials: true });
            } catch (error) {
              console.error("UnityRoom.js: error event");
            }
          }

          navigate("/therapy-rooms");
        }
      };

      window.addEventListener("message", handleUnityMessage);
      return () => window.removeEventListener("message", handleUnityMessage);
    };

    if (isValidSession && roomUrl) return setupUnityCommunication();
    return undefined;
  }, [isValidSession, navigate, roomType, roomUrl]);

  // 🔹 Invalid session fallback
  if (!isValidSession) {
    return (
      <div
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <p>Session data is missing or invalid. Please go back to Therapy Rooms.</p>
      </div>
    );
  }

  // 🔹 Main render
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        margin: 0,
        padding: 0,
        overflow: "hidden",
      }}
    >
      <Navbar
  setCurrentNickname={setCurrentNickname}
  currentNickname={currentNickname}
  setCurrentFormType={setCurrentFormType}
  handleLogout={async (...args) => {
    const isAICompanionPage = window.location.pathname.includes("ai-companion");

    if (isAICompanionPage) {

      const meetingID = sessionStorage.getItem("meetingID");
      const userId = sessionStorage.getItem("userId");

      if (meetingID && userId && !window.sentOffline) {
        window.sentOffline = true;
        const payload = { meetingID, online: false };

        try {
          navigator.sendBeacon(
            `${Env.REACT_APP_PYTHON_API_URL}/api/set-online-status/${userId}`,
            JSON.stringify(payload)
          );
        } catch (err) {
          console.error("UnityRoom.js: error event");
        }
      }
    }

    await handleLogout(...args);
  }}
  showButtons={true}
  PlutonicuserID={sessionStorage.getItem("userId")}
/>

      <style>
        {`
          @media (min-width: 1400px) {
            #unity-iframe {
              position: fixed !important;
              top: 96px !important;
              left: 0 !important;
              width: 100vw !important;
              height: calc(100vh - 95px) !important;
              transform: scale(1) !important;
              transform-origin: center center !important;
            }
          }
        `}
      </style>

      {roomUrl ? (
        <iframe
          id="unity-iframe"
          src={roomUrl}
          style={{
            position: "absolute",
            top: "80px",
            left: "0",
            width: "100vw",
            height: "calc(100vh - 80px)",
            border: "none",
            display: "block",
            overflow: "hidden",
          }}
          title="Unity WebGL Build"
          allow="fullscreen; camera; microphone; display-capture"
          onLoad={() => {
            const unityIframe = document.getElementById("unity-iframe");
            if (unityIframe && unityIframe.contentWindow) {
              // Fetch a fresh authenticated session for this frame.
              void sendUnitySession(unityIframe);
            }
          }}
        />
      ) : (
        <p>Loading Unity Room...</p>
      )}
    </div>
  );
}

export default UnityRoom;
